import React, { useEffect, useMemo, useRef } from "react";
import styles from "./LocationSelector.module.css";
import { CheckIcon } from "../ModelSettingsPopup/modelIcons";
import type { SiteMappingOption } from "../../../api";

const AUTO_SITE = "auto";
const ZEN_SITE = "ZEN";

interface LocationSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
  siteMappings: SiteMappingOption[];
  selectedSites: string[];
  onChange: (selectedSites: string[]) => void;
}

const getIsAutoOnly = (selectedSites: string[]) =>
  selectedSites.length === 1 && selectedSites[0] === AUTO_SITE;

const getDisplayName = (site: SiteMappingOption, city: string) => {
  if (site.mfilesSite === ZEN_SITE) return "Zentrale";
  return site.mfilesSiteName !== city ? site.mfilesSiteName : "";
};

const LocationSelector: React.FC<LocationSelectorProps> = ({
  isOpen,
  onClose,
  buttonRef,
  siteMappings,
  selectedSites,
  onChange,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  const { cityGroups, sortedManualSites } = useMemo(() => {
    const grouped = siteMappings.reduce(
      (acc, site) => {
        const cityKey = site.city?.trim() || "Other";
        if (!acc[cityKey]) acc[cityKey] = [];
        acc[cityKey].push(site);
        return acc;
      },
      {} as Record<string, SiteMappingOption[]>,
    );

    const sortedCities = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

    const normalizedCityGroups = sortedCities.map((city) => ({
      city,
      sites: [...grouped[city]].sort((a, b) =>
        a.mfilesSite.localeCompare(b.mfilesSite),
      ),
    }));

    return {
      cityGroups: normalizedCityGroups,
      sortedManualSites: normalizedCityGroups.flatMap((group) =>
        group.sites.map((site) => site.mfilesSite),
      ),
    };
  }, [siteMappings]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose, buttonRef]);

  if (!isOpen) return null;

  const manualSites = sortedManualSites;
  const selectedManualSites = selectedSites.filter((site) => site !== AUTO_SITE);
  const allManualSelected =
    manualSites.length > 0 && selectedManualSites.length === manualSites.length;

  const handleAutoClick = () => onChange([AUTO_SITE]);

  const handleManualToggle = (abbr: string) => {
    const isSelected = selectedManualSites.includes(abbr);
    const nextSelected = isSelected
      ? selectedManualSites.filter((site) => site !== abbr)
      : [...selectedManualSites, abbr];

    onChange(nextSelected.length > 0 ? nextSelected : [AUTO_SITE]);
  };

  const handleToggleAll = () => {
    onChange(allManualSelected ? [AUTO_SITE] : manualSites);
  };

  return (
    <div className={styles.popup} ref={popupRef}>
      <div className={styles.header}>
        <span className={styles.title}>Available locations</span>
        {manualSites.length > 0 && (
          <button
            className={`${styles.bulkAction} ${allManualSelected ? styles.bulkActionDanger : ""}`}
            onClick={handleToggleAll}
          >
            {allManualSelected ? "Deselect all" : "Select all"}
          </button>
        )}
      </div>

      <div className={styles.list}>
        <button
          className={`${styles.optionRow} ${getIsAutoOnly(selectedSites) ? styles.optionRowActive : ""}`}
          onClick={handleAutoClick}
        >
          <span className={styles.optionText}>
            <span className={styles.optionName}>Auto</span>
            <span className={styles.optionTag}>Let the system choose</span>
          </span>
          {getIsAutoOnly(selectedSites) && (
            <span className={styles.optionCheck}>
              <CheckIcon />
            </span>
          )}
        </button>

        <div className={styles.divider} />

        {cityGroups.map(({ city, sites }) => (
          <div key={city} className={styles.cityCard}>
            {sites.length > 1 && (
              <div className={styles.cityHeading}>{city}</div>
            )}
            <div className={styles.cityItems}>
              {sites.map((site) => {
                const isSelected = selectedManualSites.includes(site.mfilesSite);
                const displayName = getDisplayName(site, city);
                const showCityLabel = sites.length === 1 && displayName && displayName !== city;
                return (
                  <button
                    key={site.mfilesSite}
                    className={`${styles.optionRow} ${isSelected ? styles.optionRowActive : ""}`}
                    onClick={() => handleManualToggle(site.mfilesSite)}
                  >
                    <span className={styles.optionBadge}>{site.mfilesSite}</span>
                    <span className={styles.optionText}>
                      <span className={styles.optionName}>{displayName || city}</span>
                      {showCityLabel && (
                        <span className={styles.optionTag}>{city}</span>
                      )}
                    </span>
                    {isSelected && (
                      <span className={styles.optionCheck}>
                        <CheckIcon />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LocationSelector;
